import { useState, useEffect, useRef } from 'react';
import { Button} from '@mui/material';
import { jaJP } from '@mui/x-data-grid';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import CloseIcon from '@mui/icons-material/Close';
import BackspaceIcon from '@mui/icons-material/Backspace';
import DoneIcon from '@mui/icons-material/Done';
import { useAuth } from '../../contexts/AuthContext';
import { usePost } from '../../apis/ApiRequests';
import { CustomDataGrid } from '../../components/dataGrid/CustomDataGrid';
import CustomSnackBar from '../../components/alert/CustomSnackBar';
import CustomConfirm from '../../components/confirm/CustomConfirm';
import Header from '../../components/Header';
import Frame from '../../components/uiParts/Frame';
import TitleBar from '../../components/uiParts/TitleBar';
import MastaSearchForm from '../../components/uiParts/maintenance/MastaSearchForm';
import CustomOutlinedInput from '../../components/input/CustomOutlinedInput';
import { read, utils } from 'xlsx';

const CommentList = () => {

	//===============================================
	// initialize
	//===============================================
	//-----------------------------------------------
    // initialize - ベース情報を取得
	//-----------------------------------------------
    const { authInfo } = useAuth();
    const facilityCode = authInfo.facilityCode;
    const facilityName = authInfo.facilityName;
    const userId       = authInfo.userId;
    const userName     = authInfo.userName;
    const authority    = authInfo.authority;
    
	//-----------------------------------------------
    // initialize - useState
	//-----------------------------------------------
    const [ dataGridHeight,    setDataGridHeight    ] = useState(window.innerHeight - 250);
    const [ condition ,        setCondition         ] = useState('');
    const [ commentList,       setCommentList       ] = useState([]);
    const [ searchCount,       setSearchCount       ] = useState(0);
    const [ targetList,        setTargetList        ] = useState('');
    const [ code,              setCode              ] = useState('');
    const [ name,              setName              ] = useState('');
    const [ displayName,       setDisplayName       ] = useState('');
    const [ displayNo,         setDisplayNo         ] = useState('');
    const [ nameKana,          setNameKana          ] = useState('');
    const [ openDialog,        setOpenDialog        ] = useState(false);
    const [ openConfirm,       setOpenConfirm       ] = useState(false);
    const [ openSnackBarTrue,  setOpenSnackBarTrue  ] = useState(false);
    const [ openSnackBarFalse, setOpenSnackBarFalse ] = useState(false);
    const [ openRequire,       setOpenRequire       ] = useState(false);
    const [ requireMessage,    setRequireMessage    ] = useState('');
    const [ openConfirm_excel, setOpenConfirm_excel ] = useState(false);
    const [excelDataList,      setExcelDataList     ] = useState([]);
    const [ excelImportFlag,   setExcelImportFlag   ] = useState(false);
    const [ isProcessing,      setIsProcessing      ] = useState(false);
    const [fileName,           setFileName          ] = useState('');
    
	//-----------------------------------------------
    // initialize - useRef
	//-----------------------------------------------
    const fileInputRef = useRef();
    
	//===============================================
	// API
	//===============================================
	//-----------------------------------------------
    // API - searchData / masta_Comment
	//-----------------------------------------------
    const { response : searchData_Res, loading : searchData_Loading, post:searchData_Post } = usePost();
    const searchData = async () => {
        const postData = {
            'className'    : 'mch.models.masta.Comment',
            'methodName'   : 'searchData',
            'facilityCode' : facilityCode,
            'userId'       : userId,
            'keyword1'     : condition
        }
        await searchData_Post(postData);
    };
	//-----------------------------------------------
    // API - setData / masta_Comment
	//-----------------------------------------------
    const { response : setData_Res, loading : setData_Loading, post:setData_Post } = usePost();
    const setData = async (registerData) => {
        const postData = {
            'className'    : 'mch.models.masta.Comment',
            'methodName'   : 'setData',
            'facilityCode' : facilityCode,
            'userId'       : userId,
            'activeFlag'   : registerData.activeFlag,
            'code'         : registerData.code,
            'name'         : registerData.name,
            'displayName'  : registerData.displayName,
            'nameKana'     : registerData.nameKana,
            'displayNo'  : registerData.displayNo,
        }
        await setData_Post(postData);
    };
	//===============================================
	// useEffect
	//===============================================
	//-----------------------------------------------
    // useEffect - ロード時の設定
	//-----------------------------------------------
    useEffect(() => {

        setSearchCount(searchCount+1);
        
        const handleResize = () => {
          setDataGridHeight(window.innerHeight - 250);
        }
        window.addEventListener('resize', handleResize);
        return () => {
          window.removeEventListener('resize', handleResize);
        }
    },[]);
	//-----------------------------------------------
    // useEffect - searchData / masta_Comment
	//-----------------------------------------------
    useEffect(() => {
        setCommentList([]);
        if (searchData_Res) {
            setCommentList(searchData_Res.Data);
        }
    }, [searchData_Res]);
	//-----------------------------------------------
    // useEffect - setData / masta_Comment
	//-----------------------------------------------
    useEffect(() => {
        if (setData_Res) {
            setOpenDialog(false);
            handleCloseConfirm();
            if(!isProcessing){
                handleClickSnackBarTrue();
            }
            setSearchCount(searchCount+1);
        }
    }, [setData_Res]);
	//-----------------------------------------------
    // useEffect - 検索処理
	//-----------------------------------------------
    useEffect(() => {
        searchData();
    }, [searchCount])
	//-----------------------------------------------
    // useEffect - dataGirdの選択
	//-----------------------------------------------
    useEffect(() => {
        if (targetList) {
            setCode(targetList.code);
            setName(targetList.name);
            setDisplayName(targetList.displayName);
            setNameKana(targetList.nameKana);
            setDisplayNo(targetList.displayNo);

            setOpenDialog(true); // ダイアログを表示
        }
    }, [targetList]);
	//-----------------------------------------------
    // useEffect - エクセルファイル取り込み処理
	//-----------------------------------------------
    useEffect(() => {
        const importSequentially = async () => {
            if (!excelImportFlag) return;

            for (const item of excelDataList) {
                const registerData = {
                    'activeFlag'   : 1,
                    'code'         : item.code,
                    'name'         : item.name,
                    'displayName'  : item.displayName,
                    'nameKana'     : item.nameKana,
                    'displayNo'    : item.displayNo
                };
                
                try {
                    await setData(registerData);
                } catch (err) {
                }
            }
            
            setExcelImportFlag(false);
            setIsProcessing(false);
            handleCloseConfirm_excel();
        };

        importSequentially();
    }, [excelDataList, excelImportFlag]);

	//===============================================
	// eventHandler
	//===============================================
	//-----------------------------------------------
    // eventHandler - 日付の変更
	//-----------------------------------------------
    const handleChange_entryDate = (value) => {
    };
	//-----------------------------------------------
    // eventHandler - 検索ボタン
	//-----------------------------------------------
    const handleClick_search = () => {
        setSearchCount(searchCount+1);
    };
	//-----------------------------------------------
    // eventHandler - 検索クリアボタン
	//-----------------------------------------------
    const handleClick_search_clear = () => {
        setCondition('');
        setSearchCount(searchCount+1);
    };
	//-----------------------------------------------
    // eventHandler - 新規ボタン
	//-----------------------------------------------
    const handleClick_new = () => {
        setCode('');
        setName('');
        setDisplayName('');
        setNameKana('');
        setDisplayNo('');
        setOpenDialog(true);
    };
	//-----------------------------------------------
    // eventHandler - 登録ボタン
	//-----------------------------------------------
    const handleClick_Register = (activeFlag) => {
        const requireStatus = checkedRequire(); // 必須項目をチェックする
        if (requireStatus) {
            const registerData = {
                'activeFlag'   : activeFlag,
                'code'         : code,
                'name'         : name,
                'displayName'  : displayName,
                'nameKana'     : nameKana,
                'displayNo'    : displayNo
            }
            setData(registerData);
        } else {
            handleClickSnackBarRequire();
        }
    };
	//-----------------------------------------------
    // eventHandler - ダイアログを非表示
	//-----------------------------------------------
    const handleClick_dialog_close = () => {
        setOpenDialog(false);
    }
	//-----------------------------------------------
    // eventHandler - EXCELファイルの取り込み
	//-----------------------------------------------
    const handleFileChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setFileName(file.name);
            const reader = new FileReader();
            reader.onload = (e) => {
                const data = new Uint8Array(e.target.result);
                const workbook = read(data, { type: 'array' });
    
                const sheetName = '入札条件'; // 読み込むシート名
                const worksheet = workbook.Sheets[sheetName];
    
                if (!worksheet) {
                    setRequireMessage("指定されたシート名が存在しません。");
                    handleClickSnackBarRequire();
                    return;
                }    
                // シートからデータをJSON形式で取得
                const jsonData = utils.sheet_to_json(worksheet, { header: 1 });
                const rows = jsonData.map((row, index) => {
                    if (index === 0) return null; // ヘッダー行をスキップ
                    return {
                        code: row[0] || '',
                        name: row[1] || '',
                        displayName: row[2] || '',
                        nameKana: row[3] || '',
                        displayNo: row[4] || ''
                    };
                }).filter(row => row !== null);
    
                handleOpenConfirm_excel();
                setExcelDataList(rows);
            };
            reader.readAsArrayBuffer(file);
        }
    };
	//-----------------------------------------------
    // eventHandler - EXCELファイルの参照
	//-----------------------------------------------
    const handeliClick_excel = () => {
        fileInputRef.current.click();
    }
    const handliClick_excelImportOK = () => {
        setExcelImportFlag(true);
        setIsProcessing(true);
        setOpenSnackBarTrue(false);
    }
    //===============================================
    // dataGird
    //===============================================
    //-----------------------------------------------
    // dataGird - テーブルの見出し
    //-----------------------------------------------
    const columns = [
        { field: 'displayNo',   headerName: '表示No',     width: 100, columnSeparatorWidth: 1, type:'number', },
        { field: 'code',        headerName: 'コード',  width: 200, columnSeparatorWidth: 1, },
        { field: 'name',        headerName: '名称',    width: 300, columnSeparatorWidth: 1, },
        { field: 'displayName', headerName: '表示名称', width: 300, columnSeparatorWidth: 1, },
        { field: 'nameKana',    headerName: 'カナ名称', width: 300, columnSeparatorWidth: 1, },
    ];
    //-----------------------------------------------
    // dataGrid - 行の選択
    //-----------------------------------------------
    const handleClick_dataGrid = (event) => {
        setTargetList(event.row)
    };
	//===============================================
	// Function
	//===============================================
	//-----------------------------------------------
	// Function - 必須項目をチェックする
	//-----------------------------------------------
    const checkedRequire = () => {
        const errorFields = [];
    
        if (!code) errorFields.push('「コード」');
        if (!name) errorFields.push('「名称」');
        if (!displayName) errorFields.push('「表示名称」');
        if (!displayNo) errorFields.push('「表示No」');
    
        if (errorFields.length) {
            const text = errorFields.join('、') + 'が入力されていません';
            setRequireMessage(text);
            return false;  // エラーがある場合は false を返します
        }
        
        return true;  // エラーがない場合は true を返します
    };
	//===============================================
	// UI Message
	//===============================================
    // コンファームの表示
    const handleOpenConfirm = () => { setOpenConfirm(true) };
    
    // コンファームを閉じる
    const handleCloseConfirm = () => { setOpenConfirm(false) };

    // コンファームの表示
    const handleOpenConfirm_excel = () => { setOpenConfirm_excel(true) };
    
    // コンファームを閉じる
    const handleCloseConfirm_excel = () => { setOpenConfirm_excel(false) };
    
    // スナックバー(必須チェック)を表示
    const handleClickSnackBarRequire = () => { setOpenRequire(true) };
    
	// スナックバー(必須チェック)を閉じる
    const handleCloseSnackBarRequire = (event, reason) => {
        if (reason === 'clickaway') { return } setOpenRequire(false);
    };
	// スナックバー(正常)を表示
    const handleClickSnackBarTrue = () => { setOpenSnackBarTrue(true) };
    
	// スナックバー(正常)を閉じる
    const handleCloseSnackBarTrue = (event, reason) => {
        if (reason === 'clickaway') { return } setOpenSnackBarTrue(false);
    };
	// スナックバー(異常)を表示
    const handleClickSnackBarFalse = () => { setOpenSnackBarFalse(true) };
    
	// スナックバー(異常)を閉じる
    const handleCloseSnackBarFalse = (event, reason) => {
        if (reason === 'clickaway') { return } setOpenSnackBarFalse(false);
    };

    //===============================================
    // Css
    //===============================================
    const dialog_button_close = { 
        fontSize: '16px', height:'40px', width:'320px', color:'#fff', backgroundColor:'#808080'
    };
	//===============================================
	// JSXのレンダー
	//===============================================
    return (
        <>
            <Header loginInfo="on" backButton="on" backPage="/bidMastaMenu" setEntryDate={handleChange_entryDate} />
            <div className='commentList bodyContens'>
                <TitleBar title="入札条件マスタ一覧" />
                <div className='mainContents'>
                    <Frame classname="frame" title="マスタ検索">
                        <MastaSearchForm
                            id                = "commentList_condition"
                            value             = { condition }
                            handleChange      = { (e) => setCondition(e.target.value) }
                            handleSearch      = { () => handleClick_search() }
                            handleSearchClear = { () => handleClick_search_clear() }
                            handleClickNew    = { () => handleClick_new() }
                            excelRef            = { fileInputRef }
                            handleExcelOnchange = { (e) => handleFileChange(e) }
                            handleExcelClick    = { (e) => handeliClick_excel(e) }
                        />
                    </Frame>
                    <Frame classname="frame" title="マスタ一覧">
                        <CustomDataGrid
                            getRowClassName={(params) =>
                                params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
                            }
                            editMode                = "row"
                            rows                    = { commentList }
                            columns                 = { columns }
                            sx                      = { {height:dataGridHeight,fontSize:14,borderColor: 'primary.light',} }
                            density                 = "compact"
                            localeText              = { jaJP.components.MuiDataGrid.defaultProps.localeText }
                            showColumnRightBorder
                            showCellRightBorder
                            initialState            = {{pagination: { paginationModel: { pageSize: 100 } },}}
                            pageSizeOptions         = {[50, 100, 150, 200]}
                            loading                 = { searchData_Loading }
                            onCellClick             = {(event) => handleClick_dataGrid(event)}
                        />
                    </Frame>
                </div>
            </div>
            <div className='commentList_dialog'>
                <Dialog open={ openDialog } onClose={ handleClick_dialog_close } maxWidth='lg' >
                    <DialogTitle>
                        <TitleBar title="入札条件マスタ登録" />
                    </DialogTitle>
                    <DialogContent style={{border:'none'}} sx = {{width:'auto'}}>
                        <div style={{backgroundColor:'#efefef', padding:'10px'}}>
                            <div>
                                <CustomOutlinedInput
                                    id       = "commentList_code"
                                    label    = "コード"
                                    value    = { code }
                                    width    = "300px"
                                    onChange = {(e) => setCode(e.target.value)}
                                />
                            </div>
                            <div style={{marginTop:'10px'}}>
                                <CustomOutlinedInput
                                    id       = "commentList_name"
                                    label    = "名称"
                                    value    = { name }
                                    width    = "300px"
                                    onChange = {(e) => setName(e.target.value)}
                                />
                            </div>
                            <div style={{marginTop:'10px'}}>
                                <CustomOutlinedInput
                                    id       = "commentList_displayName"
                                    label    = "表示名称"
                                    value    = { displayName }
                                    width    = "300px"
                                    onChange = {(e) => setDisplayName(e.target.value)}
                                />
                            </div>
                            <div style={{marginTop:'10px'}}>
                                <CustomOutlinedInput
                                    id       = "commentList_nameKana"
                                    label    = "カナ名称"
                                    value    = { nameKana }
                                    width    = "300px"
                                    onChange = {(e) => setNameKana(e.target.value)}
                                />
                            </div>
                            <div style={{marginTop:'10px'}}>
                                <CustomOutlinedInput
                                    id       = "commentList_displayNo"
                                    label    = "表示No"
                                    value    = { displayNo }
                                    width    = "300px"
                                    onChange = {(e) => setDisplayNo(e.target.value)}
                                />
                            </div>
                            <div style={{display:'flex', marginRight:'10px',marginTop:'15px'}}>
                                <div style={{marginLeft:'5px'}}>
                                    <Button
                                        color     = "attention"
                                        size      = "large"
                                        variant   = "contained"
                                        sx        = {{width:'140px', color:'#fff'}}
                                        onClick   = { () => handleOpenConfirm() }
                                        startIcon = {<BackspaceIcon />}
                                    >{'削除'}
                                    </Button>
                                    
                                </div>
                                <div>
                                    <Button
                                        color     = "primary"
                                        size      = "large"
                                        variant   = "contained"
                                        sx        = {{width:'140px', marginLeft:'10px'}}
                                        onClick   = { () => handleClick_Register(1) }
                                        startIcon = {<DoneIcon />}
                                    >{'登録'}
                                    </Button>
                                </div>
                            </div>
                        </div>
                        <div style={{marginTop:'20px'}}>
                            <Button
                                color     = "tertiary"
                                size      = "large"
                                variant   = "contained"
                                sx        = { dialog_button_close }
                                onClick   = {() => handleClick_dialog_close()}
                                startIcon = {<CloseIcon />}
                            >{'閉じる'}
                            </Button>
                        </div>
                    </DialogContent>
                </Dialog>
                <CustomSnackBar
                    open            = {openSnackBarTrue}
                    onClose         = {() => handleCloseSnackBarTrue()}
                    severity        = "success"
                    backgroundColor = "#2e8b57"
                    message         = "登録しました"
                />
                <CustomSnackBar
                    open            = {openSnackBarFalse}
                    onClose         = {() => handleCloseSnackBarFalse()}
                    severity        = "error"
                    backgroundColor = "#db7093"
                    message         = "登録に失敗しました"
                />
                <CustomSnackBar
                    open            = {openRequire}
                    onClose         = {() => handleCloseSnackBarRequire()}
                    severity        = "error"
                    backgroundColor = "#db7093"
                    message         = {requireMessage}
                />
                <CustomConfirm
                    open      = {openConfirm}
                    onClose   = {() => handleCloseConfirm()}
                    onClickNG = {() => handleCloseConfirm()}
                    onClickOK = {() => handleClick_Register(0)}
                    message   = "指定データの削除を行います。よろしいですか？"
                />
                <CustomConfirm
                    open      = {openConfirm_excel}
                    onClose   = {() => handleCloseConfirm_excel()}
                    onClickNG = {() => handleCloseConfirm_excel()}
                    onClickOK = {() => handliClick_excelImportOK()}
                    isProcessing = {isProcessing}
                    message   = "エクセルファイルのアップロードを行います。よろしいですか？"
                />
            </div>
        </>
    )
}
export default CommentList;